import React, { FC, memo, useMemo } from 'react';

import Decimal from 'decimal.js';

import Theme from '@src/theme';

import { EstimateApyFarm } from '@services/api/vaults/vaults.api';

import { useEstimateVaultApy } from '@hooks/vaults/useEstimateVaultApy';

import { getSqrtPrice } from '@helpers/vaults/priceRange.helpers';

import { formatBNWithCommas, toBN } from '@utils/formaters.utils';

import SkeletonLoader from '@components/common/SkeletonLoader';
import ItemStats from '@components/vaults/ItemStats';

import { PERCENT_DIVIDER } from '@constants/common/common.constants';
import { FarmType } from '@constants/vaults/common.constants';

import { VaultDto } from '@dto/vaults/vaults.dto';

import { VaultFarmsType } from '@app-types/vaults/vault.types';
import { VaultFarmsV3InfoType } from '@app-types/vaults/vaultContext.types';

import useStyles from './styles.module';

interface Props {
  selectedV3Farm: VaultFarmsType;
  vaultFarms: VaultFarmsType[];
  vaultFarmsV3Info: VaultFarmsV3InfoType;
  minPercent: Decimal;
  maxPercent: Decimal;
  vault: VaultDto;
}

const EstimatedApy: FC<Props> = ({
  vaultFarms,
  vaultFarmsV3Info,
  selectedV3Farm,
  maxPercent,
  minPercent,
  vault,
}) => {
  const { classes } = useStyles();

  const normalizedFarms: EstimateApyFarm[] = useMemo(
    () =>
      vaultFarms.map(({ farm, farmInfo }) => {
        const { isV3 } = farmInfo;
        const { farmId, percent } = farm;

        const sqrtPriceX96 = vaultFarmsV3Info[farmId]?.sqrtPriceX96;
        const isCurrentFarmSelected = farmId === selectedV3Farm.farm.farmId;

        const args = {
          farmId,
          percent,
          type: isV3 ? FarmType.V3 : FarmType.V2,
        };

        if (isV3) {
          const min = isCurrentFarmSelected
            ? minPercent.div(PERCENT_DIVIDER)
            : toBN(vaultFarmsV3Info[farmId].initialMinPercent).div(
                PERCENT_DIVIDER,
              );

          const max = isCurrentFarmSelected
            ? maxPercent.div(PERCENT_DIVIDER)
            : toBN(vaultFarmsV3Info[farmId].initialMaxPercent).div(
                PERCENT_DIVIDER,
              );

          const sqrtPriceAX96 = getSqrtPrice(sqrtPriceX96, min);
          const sqrtPriceBX96 = getSqrtPrice(sqrtPriceX96, max);

          return {
            ...args,
            sqrtPriceAX96,
            sqrtPriceBX96,
          };
        }

        return {
          ...args,
          sqrtPriceAX96: undefined,
          sqrtPriceBX96: undefined,
        };
      }),
    [minPercent, maxPercent, selectedV3Farm, vaultFarms, vaultFarmsV3Info],
  );

  const estimateApyParams = useMemo(
    () => ({
      vaultFactoryId: selectedV3Farm.farmInfo.vaultFactoryId,
      chainId: vault.chainId,
      isDisabled: !selectedV3Farm,
      takeProfitTokenPercent: vault.takeProfitPercent * PERCENT_DIVIDER,
      dummyInvestUsd: Number(vault.tvlUsd),
      selectedFarms: normalizedFarms,
    }),
    [normalizedFarms, selectedV3Farm, vault],
  );

  const { estimatedApy, isPending: isPendingApy } =
    useEstimateVaultApy(estimateApyParams);

  return (
    <ItemStats
      title="APY:"
      value={
        isPendingApy ? (
          <SkeletonLoader className={classes.loader} />
        ) : (
          `${formatBNWithCommas(estimatedApy.apy, 2)}%`
        )
      }
      titleVariant="body2"
      valueVariant="subtitle2"
      className={classes.item}
      valueColor={Theme.primary.p3}
    />
  );
};

export default memo(EstimatedApy);
